/// <amd-module name="Build/Pipelines/PipelinesView" />
import { BuildSignalRActionCreator, BuildSignalRActionHub } from "Actions/SignalR";
import { PipelineDataList } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { INewFolderProps } from "Build/Common/Commands/NewFolder/Command.types";
import { CommandComponent, prefetch } from "Build/Common/Library/Components/CommandComponent";
import { registerErrorHandler } from "Build/Common/Library/ErrorHandler";
import { BuildRequiredPermissions, Security } from "Build/Common/Library/Security";
import { clearTreeStateOnUnmount, getTreeState } from "Build/Common/Library/StateHandler";
import { PermissionsStore } from "Build/Common/Library/Stores/Permissions";
import { ContributionDataActionHub } from "Build/Flux/Actions/ContributionData";
import { DefinitionFavoritesActionCreator, DefinitionFavoritesActionHub } from "Build/Pipelines/Actions/Favorites";
import { PipelineFilterKeys } from "Build/Pipelines/Constants";
import { Performance, disposeFPS } from "Build/Pipelines/Performance";
import { PipelinesStore } from "Build/Pipelines/PipelinesStore";
import * as Resources from "Build/Pipelines/Resources";
import { BuildSignalRHub } from "Build/Pipelines/SignalRHub";
import { PipelinesTable } from "Build/Pipelines/Tabs/Pipelines/PipelinesTable";
import { PipelinesTreeView } from "Build/Pipelines/Tabs/Pipelines/PipelinesTreeView";
import { IGeneralSettingsService } from "Build/Settings/general/GeneralSettingsService";
import { IFavoritesService } from "Favorites/Common/FavoritesService";
import { GlobalMessageLevel, IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { RunsFilterBar } from "Tabs/Runs/RunsFilterBar";
import { RunsTable } from "Tabs/Runs/RunsTable";
import { RunsAppliedFilterStore } from "Tabs/Runs/Stores/RunsAppliedFilterStore";
import { getTFSPageData, getTFSProject } from "Tfs/Platform/Page";
import { IObservableArray, IReadonlyObservableArray, ObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, IVssComponentProperties, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { IStartRightExtensionService } from "VSS/Platform/StartRightExtension";
import { Header, IHeaderBreakpoint, TitleSize } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem, getFilterItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";
import { IObservableViewStateUrl } from "VSSUI/Navigation/ViewStateNavigation";
import { IVssHubViewState, VssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, TabBar, TabContent, TabSize } from "VSSUI/Tabs";
import { InlineKeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Filter, IFilterState } from "VSSUI/Utilities/Filter";
import { ZeroData } from "VSSUI/ZeroData";
import * as React from "react";
import { INewBranchFilter } from "./Tabs/Runs/RunsFilterBar.types";

export interface IPipelinesViewContentProps extends IVssComponentProperties {}

export interface IPipelinesViewContentState {
    filterHasChanges: ObservableValue<boolean>;
    showZeroDataExperience: boolean;
    tabs: IReadonlyObservableArray<IVssContributedTab>;
    newBranches: INewBranchFilter[];
    showImport: boolean;
}

namespace Tabs {
    export const Folders = "folders";
    export const Pipelines = "pipelines";
    export const Runs = "runs";
}

class PipelinesViewContent extends VssComponent<IPipelinesViewContentProps, IPipelinesViewContentState> {
    private _isPageLoad = true;
    private _recentUrl: IObservableViewStateUrl;
    private _runsUrl: IObservableViewStateUrl;
    private _foldersUrl: IObservableViewStateUrl;
    private _permissions: PermissionsStore;
    private _security: Security;
    private _treeState: string = "";
    private _projectSettingsService: IGeneralSettingsService;

    private _headerCommandBarItems: IObservableArray<IHeaderCommandBarItem>;

    private _store: PipelinesStore;
    private _hubViewState: IVssHubViewState;
    private _favoritesActionHub: DefinitionFavoritesActionHub;
    private _favoritesActionCreator: DefinitionFavoritesActionCreator;
    private _signalRActionHub: BuildSignalRActionHub;
    private _signalRHub: BuildSignalRHub;
    private _pipelineFilter: Filter;
    private _runsAppliedFilterStore: RunsAppliedFilterStore;

    constructor(props: IPipelinesViewContentProps, context: IVssComponentContext) {
        super(props, context);

        this._favoritesActionHub = new DefinitionFavoritesActionHub();
        this._signalRActionHub = new BuildSignalRActionHub();

        const tfsPageData = getTFSPageData(context.pageContext);

        this._permissions = new PermissionsStore({
            pageContext: context.pageContext
        });

        this._security = new Security({
            projectId: tfsPageData && tfsPageData.project ? tfsPageData.project.id : "",
            store: this._permissions
        });

        this._pipelineFilter = new Filter();
        const pageHistoryService = this.context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        pageHistoryService.registerFilter(this._pipelineFilter, "pipeline", [
            {
                dataType: "string",
                filterKey: PipelineFilterKeys.NameFilter,
                historyBehavior: HistoryBehavior.replace
            }
        ]);

        this._projectSettingsService = this.context.pageContext.getService<IGeneralSettingsService>("IGeneralSettingsService");

        this._hubViewState = new VssHubViewState(context.pageContext, { defaultPivot: Tabs.Pipelines });
        this._recentUrl = this._hubViewState.createObservableUrl({ view: Tabs.Pipelines });
        this._runsUrl = this._hubViewState.createObservableUrl({ view: Tabs.Runs });
        this._foldersUrl = this._hubViewState.createObservableUrl({ view: Tabs.Folders });
        this._runsAppliedFilterStore = new RunsAppliedFilterStore(new Filter());

        const vssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        if (tfsPageData && tfsPageData.project && vssPageData) {
            this._favoritesActionCreator = new DefinitionFavoritesActionCreator({
                actionHub: this._favoritesActionHub,
                favoritesService: context.pageContext.getService<IFavoritesService>("IFavoritesService"),
                project: tfsPageData.project,
                currentUser: vssPageData.user
            });
        }

        this._treeState = getTreeState(this.context.pageContext);

        this._store = new PipelinesStore({
            pageContext: context.pageContext,
            contributionActionHub: new ContributionDataActionHub<PipelineDataList>(),
            favoritesActionHub: this._favoritesActionHub,
            signalRActionHub: this._signalRActionHub,
            pipelinesFilter: this._pipelineFilter,
            rootFolder: "\\",
            // If showing folder view on page load, the store should not treat initial data as recent definitions
            ignoreInitialData: this._hubViewState.selectedPivot.value === Tabs.Folders
        });

        if (tfsPageData && tfsPageData.project && vssPageData) {
            this._signalRHub =
                this.props.signalRHub ||
                new BuildSignalRHub({
                    actionCreator: new BuildSignalRActionCreator({ actionHub: this._signalRActionHub }),
                    hostInformation: {
                        hostId: vssPageData.hostId,
                        isHosted: vssPageData.isHosted,
                        hostPath: vssPageData.hostPath,
                        projectId: tfsPageData.project.id
                    },
                    pageContext: this.context.pageContext,
                    projectId: tfsPageData.project.id
                });
        }

        this.state = {
            filterHasChanges: new ObservableValue(false),
            showZeroDataExperience: !this._store.hasPipelines() && !this._store.hasUserDefinedFolders(),
            tabs: this._getTabs(),
            newBranches: [],
            showImport: this._projectSettingsService.data?.value?.disableClassicBuildPipelineCreation ? !this._projectSettingsService.data?.value?.disableClassicBuildPipelineCreation.enabled : false
        };

        // If the user has created folders before creating any pipelines, take them to the folder view until a pipeline is created
        if (!this._store.hasPipelines() && this._store.hasUserDefinedFolders()) {
            this._onTabChanged(Tabs.Folders);
        }

        this._headerCommandBarItems = new ObservableArray(this._getCommandBarItems(this._store.hasDeletedPipelines(), false));
    }

    public render() {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        if (contributionService.getDataProviderExceptions()["ms.vss-build-web.pipelines-data-provider"]) {
            // Exception occurred while retrieving build data so show error page instead of assuming there are no builds
            return (
                <div className="pipelines-zero-data pipelines-whole-page-centered">
                    <ZeroData
                        primaryText={Resources.ErrorMessage}
                        imagePath={getAssetLocation(this.context.pageContext, "ms.vss-build-web/pipelines/Content/no-results.png")}
                        imageAltText=""
                    />
                </div>
            )
        }

        if (this.state.showZeroDataExperience) {
            const image = getAssetLocation(this.context.pageContext, "ms.vss-build-web/pipelines/Content/no-builds.png");

            if (this._security.hasFolderPermission("\\", BuildRequiredPermissions.EditBuildDefinition)) {
                return (
                    <div className="pipelines-zero-data pipelines-whole-page-centered">
                        <ZeroData
                            primaryText={Resources.ZeroDataPipelinesPrimary}
                            imagePath={image}
                            imageAltText={Resources.ZeroDataPipelinesAltText}
                            secondaryText={Resources.ZeroDataPipelinesSecondary}
                        />
                        <HeaderCommandBar className="justify-center" items={this._getCommandBarItems(this._store.hasDeletedPipelines(), true)} />
                    </div>
                );
            } else {
                return (
                    <div className="pipelines-zero-data pipelines-whole-page-centered">
                        <ZeroData
                            primaryText={Resources.ZeroDataNoCreatePipelinesPrimary}
                            imagePath={image}
                            imageAltText={Resources.ZeroDataNoCreatePipelinesAltText}
                        />
                        <HeaderCommandBar className="justify-center" items={this._getCommandBarItems(this._store.hasDeletedPipelines(), true)} />
                    </div>
                );
            }
        }

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="pipelines-page" selectedTabId={this._hubViewState.selectedPivot} tabProviders={this.state.tabs}>
                    <Header
                        title={Resources.PipelinesHeader}
                        titleSize={TitleSize.Large}
                        commandBarItems={this._headerCommandBarItems}
                        headerBreakpoints={this._getHeaderCommandBreakpoints()}
                    />
                    <TabBar tabSize={TabSize.Tall} onSelectedTabChanged={this._onTabChanged} />
                    <TabContent />
                </Page>
            </Surface>
        );
    }

    public componentDidMount() {
        prefetch(this.context.pageContext);
        this._signalRHub.watchProject();
        this._store.addListener(this._onStoreChanged);
        this._runsAppliedFilterStore.filter.subscribe(this._onFilterChanged);
        this._projectSettingsService.data.subscribe(this._onProjectSettingsChanged);

        registerErrorHandler((error: Error) => {
            if (error) {
                const globalMessageService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
                globalMessageService.addBanner({
                    message: error.message,
                    level: GlobalMessageLevel.error
                });
            }
        });

        this._setTitle();
    }

    public componentWillUnmount() {
        this._store.removeListener(this._onStoreChanged);
        this._runsAppliedFilterStore.filter.unsubscribe(this._onFilterChanged);
        this._projectSettingsService.data.unsubscribe(this._onProjectSettingsChanged);

        this._hubViewState.dispose();
        this._store.dispose();
        this._signalRHub.dispose();
        registerErrorHandler((error: Error) => {});
        disposeFPS();

        clearTreeStateOnUnmount(this.context.pageContext);
    }

    private _onTabChanged = (tabKey: string) => {
        this._isPageLoad = false;
        new Performance(this.context.pageContext).onFPS();
        this._hubViewState.selectedPivot.value = tabKey;
        this._setTitle();
    };

    private _onStoreChanged = () => {
        if (!this._store.hasPipelines() && !this._store.hasUserDefinedFolders() && !this._store.isListFiltered()) {
            this.setState({
                showZeroDataExperience: true
            });
        } else {
            this.setState({
                showZeroDataExperience: false
            });
        }

        this._headerCommandBarItems.value = this._getCommandBarItems(this._store.hasDeletedPipelines(), false);
    };

    private _onProjectSettingsChanged = () => {
        if (this._projectSettingsService.data?.value?.disableClassicBuildPipelineCreation){
            this.setState({
                showImport: !this._projectSettingsService.data.value.disableClassicBuildPipelineCreation.enabled
            });
        }

        this._headerCommandBarItems.value = this._getCommandBarItems(this._store.hasDeletedPipelines(), false);
    }

    private _onFilterChanged = (value: IFilterState, action: string) => {
        this.state.filterHasChanges.value = this._runsAppliedFilterStore.filter.hasChangesToReset();
    };

    private _getCommandBarItems = (hasDeletedPipelines: boolean, isZeroDataCommandBar: boolean ): IHeaderCommandBarItem[] => {
        const pageContext = this.context.pageContext;
        const project = getTFSProject(pageContext);
        const linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const deletedPipelinesLink = linkingService.getDeletedPipelinesUrl();

        const startRightExtensionService = pageContext.getService<IStartRightExtensionService>("IStartRightExtensionService");
        const newPipelineLink = startRightExtensionService.getCreatePipelineUrl(pageContext) ||
                                linkingService.getNewDefinitionEditUrl();

        const commands: IHeaderCommandBarItem[] = [];
        const contributedMenuItems = getMenuItems(pageContext, "ms.vss-build-web.pipelines-header-menu", project).value;

        // TODO: This is a temporary fix to avoid having both ungoverened and goverened (by StartRight) options to create a pipeline.
        // Better to improve contribution point to allow certain extensions replace the default option.
        const startRightActionIndex = contributedMenuItems.findIndex(x => "contributionId" in x && (x as any).contributionId === "ms-1es.startright.startright-newpipeline");
        let startRightAction: IMenuItem | undefined;
        if (startRightActionIndex > -1) {
            startRightAction = contributedMenuItems[startRightActionIndex];
            contributedMenuItems.splice(startRightActionIndex, 1);
        }

        if (this._security.isMember()) {
            if (this._security.hasFolderPermission("\\", BuildRequiredPermissions.EditBuildDefinition)) {
                const newPipelineButtonImportant = true;
                const newPipelineButtonIsPrimary = true;
                const newPipelineCommandText = isZeroDataCommandBar ? Resources.ZeroDataPipelinesButtonText : Resources.NewPipeline;
                if (!startRightAction) {
                    // New pipeline
                    commands.push({
                        id: "new-pipeline-command",
                        important: newPipelineButtonImportant,
                        isPrimary: newPipelineButtonIsPrimary,
                        href: newPipelineLink,
                        onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                            onClickFPS(pageContext, newPipelineLink, true, event);
                        },
                        text: newPipelineCommandText
                    });
                } else {
                    const startRightHeaderCommandBarItem = startRightAction as IHeaderCommandBarItem;
                    startRightHeaderCommandBarItem.important = newPipelineButtonImportant;
                    startRightHeaderCommandBarItem.isPrimary = newPipelineButtonIsPrimary;
                    startRightHeaderCommandBarItem.text = newPipelineCommandText;
                    commands.push(startRightHeaderCommandBarItem);
                }

                if (isZeroDataCommandBar) {
                    // new folder
                    commands.push({
                        text: Resources.NewFolder,
                        id: "new-folder",
                        important: false,
                        onActivate: () => {
                            const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                            layoutManager.renderCallout(onDismiss => {
                                const props: INewFolderProps = {
                                    pageContext: this.context.pageContext,
                                    projectId: project ? project.id.toString() : "",
                                    parentPath: "\\",
                                    onClose: onDismiss,
                                    onFolderCreated: () => {
                                        this._store.fetchSharedData();
                                        this._onTabChanged(Tabs.Folders);
                                    }
                                };
                                return React.createElement(
                                    CommandComponent,
                                    {
                                        commandType: "ciNewFolderComponent",
                                        ...props
                                    },
                                    null
                                );
                            });
                        }
                    });
                }

                // import Pipeline
                if(this.state.showImport){
                    commands.push({
                        text: Resources.ImportAPipeline,
                        id: "import-definition",
                        important: false,
                        onActivate: () => {
                            const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                            layoutManager.renderCallout(onDismiss => {
                                // This is supported by a legacy dialog that doesn't expose a close notification,
                                // so onDismiss is ignored and the wrapping elements will be left in the tree
                                return React.createElement(
                                    CommandComponent,
                                    {
                                        commandType: "ciImportDefinitionComponent"
                                    },
                                    null
                                );
                            });
                        }
                    });    
                }
            }

            // security
            commands.push({
                id: "pipeline-security-command",
                text: Resources.Security,
                important: isZeroDataCommandBar && !this._security.hasFolderPermission("\\", BuildRequiredPermissions.EditBuildDefinition),
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                    layoutManager.renderCallout(onDismiss => {
                        return React.createElement(
                            CommandComponent,
                            {
                                commandType: "ciPermissionsComponent"
                            },
                            null
                        );
                    });
                }
            });

            if (hasDeletedPipelines) {
                commands.push({
                    id: "deleted-pipelines-command",
                    text: Resources.ViewDeletedPipelines,
                    important: false,
                    href: deletedPipelinesLink,
                    onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                        onClickFPS(this.context.pageContext, deletedPipelinesLink, true, event);
                    }
                });
            }
        }

        contributedMenuItems.forEach(menuItem => {
            const headerMenuItem = (menuItem as IHeaderCommandBarItem);
            commands.push(headerMenuItem);
        });

        return commands;
    };

    private _getHeaderCommandBreakpoints = (): IHeaderBreakpoint[] => {
        return [
            {
                breakpoint: ScreenBreakpoints.xsmall,
                commandBarItems: [
                    {
                        id: "new-pipeline-command",
                        important: true,
                        iconProps: undefined,
                        text: Resources.NewPipelineShort
                    }
                ]
            },
            {
                breakpoint: ScreenBreakpoints.small,
                commandBarItems: [
                    {
                        id: "new-pipeline-command",
                        important: true,
                        iconProps: undefined,
                        text: Resources.NewPipeline
                    }
                ]
            }
        ];
    };

    private _getNewBranches = (branches: INewBranchFilter[]) => {
        this.setState({
            newBranches: branches
        });
    };

    private _getTabs = (): IReadonlyObservableArray<IVssContributedTab> => {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const historyState = historyService.getState();

        // the PageStateHistoryService will add our filter state into the URL, with each key being named something like "statusFilter"
        // if any of these keys exist, we should show the filterbar so it can be initialized with this state
        const filterBarInitiallyVisible = Object.keys(historyState).some(key => key.endsWith("Filter") && key !== "pipelineNameFilter");

        const showFilterBar = new ObservableValue(filterBarInitiallyVisible);

        const tabs = new ObservableCollection<IVssContributedTab>();
        tabs.push([
            {
                id: Tabs.Pipelines,
                name: Resources.RecentTabName,
                url: this._recentUrl.value,
                order: 1,
                render: () => (
                    <div className="page-content page-content-top">
                        <PipelinesTable
                            store={this._store}
                            security={this._security}
                            favoritesActionCreator={this._favoritesActionCreator}
                            isPageLoad={this._isPageLoad}
                        />
                    </div>
                ),
                renderFilterBar: (inTabBar: boolean) => {
                    if (inTabBar) {
                        return (
                            <div className="definitions-filterbar flex-row">
                                <InlineKeywordFilterBarItem
                                    filter={this._pipelineFilter}
                                    placeholder={Resources.PipelinesFilterPlaceholderText}
                                    filterItemKey={PipelineFilterKeys.NameFilter}
                                    maxTextLength={50}
                                />
                            </div>
                        );
                    }
                }
            },
            {
                id: Tabs.Folders,
                name: Resources.FoldersTabName,
                order: 2,
                url: this._foldersUrl.value,
                render: () => (
                    <div className="page-content page-content-top">
                        <PipelinesTreeView
                            store={this._store}
                            signalRHub={this._signalRActionHub}
                            security={this._security}
                            favoritesActionCreator={this._favoritesActionCreator}
                            isPageLoad={this._isPageLoad}
                            treeState={this._treeState}
                            rootPath="\"
                        />
                    </div>
                ),
                renderFilterBar: (inTabBar: boolean) => {
                    if (inTabBar) {
                        return (
                            <div className="definitions-filterbar flex-row">
                                <InlineKeywordFilterBarItem
                                    filter={this._pipelineFilter}
                                    placeholder={Resources.PipelinesFilterPlaceholderText}
                                    filterItemKey={PipelineFilterKeys.NameFilter}
                                    maxTextLength={50}
                                />
                            </div>
                        );
                    }
                }
            },
            {
                id: Tabs.Runs,
                name: Resources.RunsTabName,
                order: 3,
                url: this._runsUrl.value,
                render: () => (
                    <>
                        <Observer showFilterBar={showFilterBar}>
                            {(props: { showFilterBar: boolean }) => {
                                if (props.showFilterBar) {
                                    return (
                                        <RunsFilterBar
                                            appliedFilterStore={this._runsAppliedFilterStore}
                                            definitionId={undefined}
                                            newBranches={this.state.newBranches}
                                        />
                                    );
                                }
                                return <></>;
                            }}
                        </Observer>
                        <div className="page-content page-content-top">
                            <RunsTable
                                appliedFilterStore={this._runsAppliedFilterStore}
                                isPageLoad={this._isPageLoad}
                                getBranchesOnNewRun={this._getNewBranches}
                                signalRActionHub={this._signalRActionHub}
                                security={this._security}
                            />
                        </div>
                    </>
                ),
                renderFilterBar: inTabBar => {
                    if (inTabBar) {
                        return (
                            <Observer filterHasChanges={this.state.filterHasChanges}>
                                {(props: { filterHasChanges: boolean }) => {
                                    return (
                                        <HeaderCommandBar
                                            items={[getFilterItem(() => (showFilterBar.value = !showFilterBar.value), props.filterHasChanges)]}
                                        />
                                    );
                                }}
                            </Observer>
                        );
                    }
                }
            }
        ] as IVssContributedTab[]);

        return tabs;
    };

    private _onZeroDataButtonClick = () => {
        const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const link = linkingService.getNewDefinitionEditUrl();
        onClickFPS(this.context.pageContext, link, true);
    };

    private _setTitle() {
        const tab = this._hubViewState.selectedPivot.value;
        if (tab === Tabs.Pipelines) {
            document.title = Resources.TitleRecent;
        } else if (tab === Tabs.Folders) {
            document.title = Resources.TitleFolders;
        } else if (tab === Tabs.Runs) {
            document.title = Resources.TitleRuns;
        } else {
            document.title = format(Resources.Title, tab);
        }
    }
}

Components.add("pipelines-hub", PipelinesViewContent);
